import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { FormField } from '@/components/ui/FormField';
import { Card } from '@/components/ui/Card';
import { useGenerateKey } from '@/api/hooks/useKeys';
import { ApiException } from '@/api/client';

export function KeyNewPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const generate = useGenerateKey();
  const [keyAlias, setKeyAlias] = useState('');
  const [userId, setUserId] = useState(params.get('user_id') ?? '');
  const [teamId, setTeamId] = useState(params.get('team_id') ?? '');
  const [modelsText, setModelsText] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [duration, setDuration] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const models = modelsText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    generate.mutate(
      {
        key_alias: keyAlias || undefined,
        user_id: userId || undefined,
        team_id: teamId || undefined,
        models: models.length ? models : undefined,
        max_budget: maxBudget ? Number(maxBudget) : undefined,
        duration: duration || undefined,
      },
      {
        onSuccess: (data: { key: string }) => {
          setGeneratedKey(data.key);
        },
        onError: (e) =>
          setSubmitError(e instanceof ApiException ? e.message : 'Generate failed'),
      },
    );
  };

  if (generatedKey) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 700 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Key Generated</h1>
        <Card>
          <div
            style={{
              padding: 16,
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 6,
              marginBottom: 16,
              color: '#166534',
              fontSize: 13,
            }}
          >
            Save this key now — it won't be shown again in full.
          </div>
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: 13,
              padding: 12,
              background: '#f8fafc',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              wordBreak: 'break-all',
              marginBottom: 16,
            }}
          >
            {generatedKey}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => navigator.clipboard.writeText(generatedKey)}>
              Copy Key
            </Button>
            <Button variant="outline" onClick={() => navigate('/keys')}>
              Done
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Generate Key</h1>
      <Card>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {submitError && (
            <div
              style={{
                padding: 12,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 6,
                color: '#991b1b',
                fontSize: 13,
              }}
            >
              {submitError}
            </div>
          )}
          <FormField label="Alias (optional)" htmlFor="key_alias">
            <Input
              id="key_alias"
              value={keyAlias}
              onChange={(e) => setKeyAlias(e.target.value)}
              placeholder="my-app-key"
            />
          </FormField>
          <FormField label="User ID (optional)" htmlFor="user_id">
            <Input
              id="user_id"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </FormField>
          <FormField label="Team ID (optional)" htmlFor="team_id">
            <Input
              id="team_id"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
            />
          </FormField>
          <FormField
            label="Models (optional)"
            htmlFor="models"
            hint="Comma or newline separated. Empty = all models."
          >
            <Textarea
              id="models"
              value={modelsText}
              onChange={(e) => setModelsText(e.target.value)}
              placeholder="gpt-4o, gpt-4o-mini"
            />
          </FormField>
          <FormField label="Max Budget (optional)" htmlFor="max_budget">
            <Input
              id="max_budget"
              type="number"
              value={maxBudget}
              onChange={(e) => setMaxBudget(e.target.value)}
              placeholder="100"
            />
          </FormField>
          <FormField
            label="Duration (optional)"
            htmlFor="duration"
            hint="e.g. 30d, 24h, 60m"
          >
            <Input
              id="duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="30d"
            />
          </FormField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={() => navigate('/keys')}>
              Cancel
            </Button>
            <Button type="submit" disabled={generate.isPending}>
              {generate.isPending ? 'Generating...' : 'Generate'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}